import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";

const WS_URL =
  (import.meta.env.VITE_WS_URL as string | undefined) ??
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws`;

type RealtimeMessage = {
  type: "process" | "event" | "hello" | "supplier_notify" | "new_item" | "item_awarded" | "moderation" | "notification";
  data?: unknown;
};

interface Ctx {
  connected: boolean;
  lastEvent: RealtimeMessage | null;
}

const RealtimeContext = createContext<Ctx>({ connected: false, lastEvent: null });

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<RealtimeMessage | null>(null);
  const qc = useQueryClient();
  const { token } = useAuth();

  useEffect(() => {
    let ws: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let stopped = false;

    // Сервер требует JWT для любого клиента (internal — полный поток,
    // supplier — персональный канал). Без токена не подключаемся вовсе.
    const url = token ? `${WS_URL}?token=${encodeURIComponent(token)}` : null;

    const connect = () => {
      if (!url || stopped) return;
      ws = new WebSocket(url);
      ws.onopen = () => {
        attempts = 0;
        setConnected(true);
      };
      ws.onclose = (ev) => {
        setConnected(false);
        if (stopped) return;
        // 4401 — сервер отклонил токен: ретраи бессмысленны (шторм 403),
        // токен протух — чистим и уходим на логин
        if (ev.code === 4401) {
          try {
            localStorage.removeItem("rus_ii_token");
          } catch {
            // ignore
          }
          window.location.assign("/login");
          return;
        }
        // экспоненциальный backoff с джиттером: 1s → 2s → 4s → … → 30s
        attempts += 1;
        const base = Math.min(30_000, 1_000 * 2 ** Math.min(attempts, 5));
        const jitter = Math.floor(Math.random() * 500);
        retry = setTimeout(connect, base + jitter);
      };
      ws.onerror = () => ws?.close();
      ws.onmessage = (msg) => {
        try {
          const parsed = JSON.parse(msg.data) as RealtimeMessage;
          setLastEvent(parsed);
          const pid = (parsed.data as { process_id?: string } | undefined)?.process_id;
          if (parsed.type === "process") {
            qc.invalidateQueries({ queryKey: ["processes"] });
            if (pid) qc.invalidateQueries({ queryKey: ["process", pid] });
          } else if (parsed.type === "event") {
            qc.invalidateQueries({ queryKey: ["events"] });
            const eventType = (parsed.data as { event_type?: string } | undefined)?.event_type;
            if (eventType?.startsWith("item.")) {
              // The other role must see quote withdrawal/change before deciding,
              // without requiring a page reload or relying only on local mutations.
              for (const queryKey of [["award-approvals"], ["item-board"], ["item-offers"],
                ["portal", "items"], ["portal", "offers"], ["portal", "awards"]]) {
                qc.invalidateQueries({ queryKey });
              }
            }
            if (pid) {
              qc.invalidateQueries({ queryKey: ["events", pid] });
              qc.invalidateQueries({ queryKey: ["process", pid] });
            }
          } else if (parsed.type === "notification") {
            // Личное уведомление: сервер шлёт его до коммита строки, поэтому
            // перечитываем чуть позже, чтобы не получить список без неё.
            setTimeout(() => {
              qc.invalidateQueries({ queryKey: ["notifications"] });
              qc.invalidateQueries({ queryKey: ["discussion-unread"] });
            }, 400);
          } else if (parsed.type === "new_item") {
            qc.invalidateQueries({ queryKey: ["portal", "items"] });
          } else if (parsed.type === "item_awarded") {
            qc.invalidateQueries({ queryKey: ["portal", "items"] });
            qc.invalidateQueries({ queryKey: ["portal", "offers"] });
            qc.invalidateQueries({ queryKey: ["portal", "awards"] });
          } else if (parsed.type === "moderation" || parsed.type === "supplier_notify") {
            qc.invalidateQueries({ queryKey: ["portal", "profile"] });
            qc.invalidateQueries({ queryKey: ["portal", "items"] });
          }
        } catch {
          // ignore
        }
      };
    };
    connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      ws?.close();
    };
  }, [qc, token]);

  return (
    <RealtimeContext.Provider value={{ connected, lastEvent }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  return useContext(RealtimeContext);
}

/**
 * Интервал polling для useQuery: при живом WS-соединении polling не нужен
 * (обновления приходят по push), возвращаем false. При потере соединения —
 * возвращаемся к polling как fallback.
 */
export function usePollingInterval(fallbackMs: number): number | false {
  const { connected } = useRealtime();
  return connected ? false : fallbackMs;
}
